import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { Typography, Checkbox } from "@mui/material";

// context
import { useCurrentResultContext } from "../../../services/context/current-result-context";

// components
import { AccordionStyled } from "./accordion";
import { ChartPercentage } from "./chart-percentage";
import { formatArea, formatCost } from "../../../utils/formatting";
import { TEXT } from "../../../constants/text";
import { DropdownSimple } from "../../ui/dropdown-simple";
import { TooltipWithIconCustom } from "../../ui/tooltip-custom";
import {
  CAR_PARKING_AREA_TYPE,
  OFFICE_TYPE,
} from "../../../constants/global.constants";
import { useResultsContext } from "../../../services/context/results-context";
import { useUserContext } from "../../../services/context/user-context";
import { getAxisGridOptions } from "../../../utils/grid-utils";

// svg
import { ReactComponent as IconBuildingsArea } from "../../../assets/svg/kpi-building-area.svg";
import { ReactComponent as IconTotalBuiltArea } from "../../../assets/svg/totalbuilt-area.svg";
import { ReactComponent as IconBuildingHeight } from "../../../assets/svg/building-height.svg";
import { ReactComponent as IconFunctionalType } from "../../../assets/svg/settings.svg";
import { ReactComponent as IconDockingStation } from "../../../assets/svg/dock-2.svg";
import { ReactComponent as IconCost } from "../../../assets/svg/cost-3.svg";
import { ReactComponent as IconAxisGrid } from "../../../assets/svg/axis-grid-4.svg";
import { ReactComponent as IconDimensions } from "../../../assets/svg/dimensions-3.svg";


export const TabAreaDetails = ({
  selectedResult,
  selectedBuilding,
  totalBuildingsArea,
  showGrid,
  setShowGrid,
  getSelectedGridIndex,
  setSelectedGridIndex,
}) => {
  const { userData } = useUserContext();
  const { buildingsTypes } = useResultsContext();

  const selectedBuildingType = buildingsTypes.find(
    (b) => b.id === selectedBuilding?.buildingTypeId,
  )?.node_name;

  const axisGridOptions = getAxisGridOptions(selectedBuilding);

  const currentAxisGrid =
    selectedBuilding && selectedResult
      ? getSelectedGridIndex(
          selectedResult.resultId,
          selectedBuilding.buildingId,
        )
      : 0;

  const areaPercentage =
    selectedBuilding?.kpis?.area && totalBuildingsArea
      ? ((selectedBuilding.kpis.area / totalBuildingsArea) * 100).toFixed(0)
      : null;

  const kpis = [
    {
      id: "kpi-1",
      name: "Function Type",
      unit: "",
      value: selectedBuilding?.type,
      icon: "function_type",
      tooltip: null,
    },
    {
      id: "kpi-2",
      name:
        selectedBuildingType === CAR_PARKING_AREA_TYPE
          ? TEXT.sidebarResults.kpis.buildingAreaCarParkingTitle
          : TEXT.sidebarResults.kpis.buildingAreaTitle,
      unit: formatArea(selectedBuilding?.kpis?.area)?.unit,
      value: formatArea(selectedBuilding?.kpis?.area)?.value,
      icon: "building_area",
      tooltip:
        selectedBuildingType === OFFICE_TYPE
          ? TEXT.sidebarResults.kpis.buildingAreaOfficeTooltip
          : selectedBuildingType === CAR_PARKING_AREA_TYPE
            ? TEXT.sidebarResults.kpis.buildingAreaCarParkingTooltip
            : TEXT.sidebarResults.kpis.buildingAreaTooltip,
    },
    /*     {
      id: "kpi-3",
      name: "% in relation to the other areas",
      unit: "%",
      value: selectedBuilding?.kpis?.area.toFixed(2),
      icon: "building_area",
    }, */
    {
      id: "kpi-4",
      name: TEXT.sidebarResults.kpis.heightTitle,
      unit: "m",
      value: selectedBuilding?.kpis?.height,
      icon: "building_height",
      tooltip: TEXT.sidebarResults.kpis.heightTooltip,
    },
    {
      id: "kpi-5",
      name: TEXT.sidebarResults.kpis.dockingSationsTitle,
      unit: "",
      value: selectedBuilding?.docks,
      icon: "docking_stations",
      tooltip: TEXT.sidebarResults.kpis.dockingSationsTooltip,
    },
    {
      id: "kpi-6",
      name: TEXT.sidebarResults.kpis.costTitle,
      unit: "€",
      value: formatCost(selectedBuilding?.kpis?.cost)?.value,
      icon: "cost",
      tooltip:
        selectedBuildingType === CAR_PARKING_AREA_TYPE
          ? TEXT.sidebarResults.kpis.costCarParkingTooltip
          : TEXT.sidebarResults.kpis.costTooltip,
    },
  ];

  const icons = {
    function_type: (
      <IconFunctionalType fill="#343745" width="18px" height="18px" />
    ),
    total_build_area: (
      <IconTotalBuiltArea fill="#343745" width="18px" height="18px" />
    ),
    building_area: (
      <IconBuildingsArea fill="#343745" width="18px" height="18px" />
    ),
    building_height: (
      <IconBuildingHeight fill="#343745" width="18px" height="18px" />
    ),
    docking_stations: (
      <IconDockingStation fill="#343745" width="18px" height="20px" />
    ),
    cost: <IconCost fill="#343745" width="18px" height="18px" />,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        /* p: "12px", */
      }}
    >
      <Box p="16px" bgcolor={selectedBuilding.color}>
        <Typography color="#fff">{selectedBuilding.name}</Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "22px",
          width: "100%",
          p: "16px",
        }}
      >
        {kpis.map((kpi) =>
          kpi.value && kpi.value !== -1 ? (
            <Box
              key={kpi.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                }}
              >
                {icons[kpi.icon]}
                {kpi.tooltip ? (
                  <TooltipWithIconCustom text={kpi.tooltip}>
                    <Typography
                      color="#343745"
                      fontSize="14px"
                      fontWeight={700}
                    >
                      {kpi.name}
                    </Typography>
                  </TooltipWithIconCustom>
                ) : (
                  <Typography color="#343745" fontSize="14px" fontWeight={700}>
                    {kpi.name}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <Typography color="#5D5F6A" fontSize={"16px"}>
                  {kpi.value}{" "}
                  <span style={{ fontSize: "12px" }}>{kpi.unit}</span>
                </Typography>

                {kpi.id === "kpi-2" && areaPercentage && (
                  <ChartPercentage value={areaPercentage} />
                )}
              </Box>
            </Box>
          ) : null,
        )}

        {selectedBuilding.axisGrid &&
        selectedBuilding.axisGrid[0] !== -1 &&
        selectedBuilding.axisGrid[1] !== -1 ? (
          <Box
            key={"axis-grid"}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
              }}
            >
              <IconDimensions fill="#343745" width="18px" height="18px" />

              {/*  <TooltipWithIconCustom
                text={TEXT.sidebarResults.kpis.dimensionsTooltip}
              > */}
              <Typography color="#343745" fontSize="14px" fontWeight={700}>
                {TEXT.sidebarResults.kpis.dimensions}
              </Typography>
              {/*  </TooltipWithIconCustom> */}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
              }}
            >
              <Typography color="#5D5F6A" fontSize={"16px"}>
                {selectedBuilding.axisGrid[0].toFixed()}{" "}
                <span style={{ fontSize: "12px" }}>m</span>
              </Typography>
              <Typography color="#5D5F6A" fontSize={"16px"}>
                :
              </Typography>
              <Typography color="#5D5F6A" fontSize={"16px"}>
                {selectedBuilding.axisGrid[1].toFixed()}{" "}
                <span style={{ fontSize: "12px" }}>m</span>
              </Typography>
            </Box>
          </Box>
        ) : null}

        {axisGridOptions.length > 0 ? (
          // Axis Grid Options
          <Box
            key={"axis-grid-options"}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <IconAxisGrid fill="#343745" width="18px" height="18px" />

                {/*  <TooltipWithIconCustom
                  text={TEXT.sidebarResults.kpis.dimensionsTooltip}
                > */}
                <Typography
                  color={showGrid ? "#343745" : "#B0B0B0"}
                  fontSize="14px"
                  fontWeight={700}
                >
                  {TEXT.sidebarResults.kpis.axisGrid}
                </Typography>
                {/*  </TooltipWithIconCustom> */}
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Typography
                  color={showGrid ? "#343745" : "#B0B0B0"}
                  fontSize="14px"
                  fontWeight={700}
                >
                  {"grid"}
                </Typography>
                <Checkbox
                  disableRipple
                  checked={showGrid}
                  onChange={() => setShowGrid((prev) => !prev)}
                  // name={}
                  inputProps={{ "aria-label": "controlled" }}
                  color="secondary"
                  sx={{
                    "&.MuiCheckbox-root": {
                      color: "#dbdbeb",
                      "&.Mui-checked": { color: "#343745" },
                      padding: 0,
                    },
                  }}
                />
              </span>
            </Box>
            {showGrid && (
              <DropdownSimple
                key={"dropdown-grid-options"}
                title={TEXT.sidebarResults.kpis.currentAxisGrid}
                list={axisGridOptions.map((option, index) => ({
                  id: index,
                  value: `${option.grid_size[0].toFixed(1)} m x ${option.grid_size[1].toFixed(1)} m`,
                }))}
                name={"axis grids"}
                setValue={(index) => {
                  setSelectedGridIndex(
                    selectedResult.resultId,
                    selectedBuilding.buildingId,
                    index,
                  );
                }}
                value={currentAxisGrid}
                tooltip={TEXT.sidebarResults.kpis.currentAxisGridTooltip}
              />
            )}
          </Box>
        ) : null}

        {userData.isAdmin && (
          <AccordionStyled title={"Admin"} expandedDefault={false}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
                p: "8px",
              }}
            >
              <Typography>AreaMin: {selectedBuilding?.range[0]}</Typography>
              <Typography>AreaMax: {selectedBuilding?.range[1]}</Typography>
              <Typography>
                Docking Stations: {selectedBuilding?.docks}
              </Typography>
              <Typography>Cost: {selectedBuilding?.cost}</Typography>
              <Typography>Height: {selectedBuilding?.height}</Typography>
              <Typography>Stories: {selectedBuilding?.stories}</Typography>
              <Typography>
                Isolated: {selectedBuilding?.isolated?.toString()}
              </Typography>
            </Box>
          </AccordionStyled>
        )}
      </Box>
    </Box>
  );
};
